import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  Panel,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useOntologyGraph } from '@/hooks/useOntologyGraph';
import { userAppContext } from '@/context/Context';
import type { UserAppContextType } from '@/router/types';
import { buildDiseaseOntologyTree } from '@/utils/ontologyTree/buildDiseaseOntologyTree';
import { useTreeLayout } from './useTreeLayout';
import { DiseaseNode } from './nodes/DiseaseNode';
import { ActionNode } from './nodes/ActionNode';
import { OntologyMoreList } from '@/components/OntologyGraph/OntologyMoreList';
import type { OntologyFlowNode } from '@/utils/ontologyTree/types';
import type { Edge } from '@xyflow/react';

const NODE_TYPES: NodeTypes = {
  disease: DiseaseNode as NodeTypes[string],
  action: ActionNode as NodeTypes[string],
};

type DiseaseOntologyTreeProps = {
  rootNodeId?: string;
  rootNodeType?: 'id' | 'name';
};

export function DiseaseOntologyTree({ rootNodeId, rootNodeType = 'id' }: DiseaseOntologyTreeProps) {
  const navigate = useNavigate();
  const { restrictSources } = userAppContext() as unknown as UserAppContextType;

  const {
    nodes: ontologyNodes,
    edges: ontologyEdges,
    initialize,
    loading,
    error,
    onExpandNode,
    collapseNode,
    moreListParentId,
    moreListChildren,
    setMoreListParentId,
    openMoreList,
    onSelectMoreChildren,
  } = useOntologyGraph({ rootNodeId, rootNodeType, moreThreshold: 10, loadGenes: false });

  useEffect(() => {
    if (!rootNodeId) return;
    void initialize();
  }, [rootNodeId, initialize]);

  const handleNavigate = useCallback(
    (nodeId: string) => {
      const source = restrictSources === 'CURATED' ? 'CURATED' : 'ALL';
      navigate({
        pathname: '/search',
        search: `?view=DISEASES&idents=${encodeURIComponent(nodeId)}&source=${source}&tab=GDA`,
      });
    },
    [navigate, restrictSources],
  );

  const handleToggle = useCallback(
    (nodeId: string) => {
      onExpandNode(nodeId);
    },
    [onExpandNode],
  );

  const { rawFlowNodes, rawFlowEdges } = useMemo(() => {
    const { flowNodes, flowEdges } = buildDiseaseOntologyTree({
      nodes: ontologyNodes,
      edges: ontologyEdges,
      onToggle: handleToggle,
      onCollapse: collapseNode,
      onNavigate: handleNavigate,
      openMoreList,
    });

    return { rawFlowNodes: flowNodes, rawFlowEdges: flowEdges };
  }, [ontologyNodes, ontologyEdges, handleToggle, collapseNode, handleNavigate, openMoreList]);

  const layoutedNodes = useTreeLayout(rawFlowNodes, rawFlowEdges);

  const [nodes, setNodes, onNodesChange] = useNodesState<OntologyFlowNode>(layoutedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(rawFlowEdges);

  useEffect(() => {
    setNodes(layoutedNodes);
  }, [layoutedNodes, setNodes]);

  useEffect(() => {
    setEdges(rawFlowEdges);
  }, [rawFlowEdges, setEdges]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {loading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.85)',
            zIndex: 10,
            fontSize: 14,
            color: '#555',
            gap: 10,
          }}
        >
          <span
            style={{
              width: 20,
              height: 20,
              border: '2px solid #4a90d9',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 0.7s linear infinite',
              display: 'inline-block',
            }}
          />
          Loading ontology…
        </div>
      )}
      {error && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#c00',
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}
      {!rootNodeId && !loading && !error && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4a5b6b',
            fontSize: 14,
            textAlign: 'center',
            padding: 24,
          }}
        >
          Search a disease by ID or name to load the hierarchy.
        </div>
      )}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2.5}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
        proOptions={{ hideAttribution: true }}
        style={{ background: '#f8fafc' }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#dde4ed" />
        <Controls style={{ background: 'transparent', border: 'none', boxShadow: 'none', gap: 4 }} />
        <MiniMap
          nodeColor={() => '#4a90d9'}
          style={{ bottom: 16, right: 16, width: 130, height: 82 }}
          zoomable
          pannable
        />
        <Panel position="top-left" style={{ width: 'fit-content' }}>
          <div
            style={{
              background: 'rgba(255,255,255,0.9)',
              border: '1px solid #e0e8f0',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 11,
              color: '#555',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              whiteSpace: 'nowrap',
            }}
          >
            <LegendItem color="#4a90d9" label="Disease" />
          </div>
        </Panel>
      </ReactFlow>

      {moreListParentId && (
        <OntologyMoreList
          parentId={moreListParentId}
          items={moreListChildren}
          onSelect={onSelectMoreChildren}
          onClose={() => setMoreListParentId(null)}
        />
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .react-flow__controls {
          bottom: 56px !important;
        }
        .react-flow__controls-button {
          border-radius: 50% !important;
          width: 28px !important;
          height: 28px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.14) !important;
          border: 1px solid #e0e8f0 !important;
          background: #fff !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .react-flow__controls-button:hover {
          background: #f0f7ff !important;
        }
        .react-flow__controls-button svg {
          max-width: 12px;
          max-height: 12px;
        }
      `}</style>
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <span
        style={{
          width: 9,
          height: 9,
          borderRadius: '50%',
          background: color,
          flexShrink: 0,
        }}
      />
      <span>{label}</span>
    </div>
  );
}
